import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppDataTable from '../../shared/AppDataTable.js';
import { Fetch, ageInMonths, DateToAgeCategory } from '../../utils/Utils.js';
import BreedDropdown from '../../shared/dropdowns/BreedDropdown';
import SexDropdown from '../../shared/dropdowns/SexDropdown';
import AreaDropdown from '../../shared/dropdowns/AreaDropdown';
import DeathCauseDropdown from '../../shared/dropdowns/DeathCauseDropdown';
import DateInput from '../../shared/inputs/DateInput.js';
import HerdExpandedComponent from '../HerdExpandedComponent.js';
import HerdCardList from '../births/HerdCardList.js';
import '../ListHerd.css';
import '../HerdExpandedComponent.css';

const formatLotCode = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '—';

const ageCategoryToMonthRange = (category) => {
  switch (category) {
    case '0-12':  return [0,  12];
    case '13-24': return [13, 24];
    case '25-36': return [25, 36];
    case '37+':   return [37, 999];
    default:      return null;
  }
};

// Wrap row so its _id is the birth event's, not the underlying lots.animals
// membership row's — historically load-bearing for a notes-patch keyed on
// _id, since removed (AnimalNotes and every write in HerdExpandedComponent
// now go by row.tag instead, unaffected by this remapping). Nothing downstream
// currently depends on which _id ends up here (both HerdCard and
// HerdExpandedComponent only ever use it as a React key), but this is kept
// as-is rather than removed speculatively — not verified safe to drop.
const asBirthRow = (row) => ({ ...row, _id: row.birth_id });

// Inline tag editor — mainly for replacing a temporary CALF-NNNN tag (see
// animals.NextTempTag, posso-help-api) with a real ear tag once the animal
// is physically tagged, though it renames any tag. Unlike the breed/sex
// dropdowns (which PATCH on every selection), this only commits on
// blur/Enter and only if the value actually changed — a free-text rename is
// a bigger, less undo-able operation than picking from a fixed option list,
// so it shouldn't fire mid-keystroke.
function TagCell({ tag, onRenamed }) {
  const [value, setValue] = useState(tag);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setValue(tag); setError(''); }, [tag]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === tag) {
      setValue(tag);
      return;
    }
    setSaving(true);
    setError('');
    Fetch(`/api/animals/${tag}/tag`, 'PATCH', JSON.stringify({ new_tag: trimmed }),
      () => { setSaving(false); onRenamed(); },
      () => { setSaving(false); setValue(tag); setError('tag_rename_failed'); }
    );
  };

  return (
    <div className="tag-cell">
      <input
        className="tag-cell-input"
        value={value}
        disabled={saving}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      />
      {error && <span className="tag-cell-error" title="Could not rename — the new tag may already be in use">!</span>}
    </div>
  );
}

const ListHerdAnimals = ({ initialLotFilter, initialSexFilter }) => {
  const { t } = useTranslation();

  const [data,               setData]               = useState([]);
  const [lots,               setLots]               = useState([]);
  const [areas,              setAreas]              = useState([]);
  const [breeds,             setBreeds]             = useState([]);
  const [isMobile,           setIsMobile]           = useState(window.innerWidth < 768);
  const [filtersOpen,        setFiltersOpen]        = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const [expandedRowIds,     setExpandedRowIds]     = useState(new Set());

  // Fetch filters
  const [lotFilter,         setLotFilter]         = useState(initialLotFilter ? String(initialLotFilter) : '');
  const [tagFilter,         setTagFilter]         = useState('');
  const [isAliveFilter,     setIsAliveFilter]     = useState('true');
  const [pregnantFilter,    setPregnantFilter]    = useState('');
  const [breedFilter,       setBreedFilter]       = useState('');
  const [sexFilter,         setSexFilter]         = useState(initialSexFilter || '');
  const [areaFilter,        setAreaFilter]        = useState('');
  const [ageFilter,         setAgeFilter]         = useState('');

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Fetch('api/data/lots?search_fields=type&search_values=herd', 'get', null,
      (d) => setLots([...(d || [])].sort((a, b) => b.lot_id - a.lot_id))
    );
    Fetch('api/data/areas', 'get', null, (d) => setAreas(d || []));
    Fetch('api/data/breeds', 'get', null, (d) => setBreeds(d || []));
  }, []);

  // Every keystroke in the tag filter fires a new request (no debounce, by
  // design — see the corral-manager equivalent this mirrors), so requests
  // can resolve out of order: a slow early response (e.g. "5", matching
  // hundreds of rows) can land after a fast later one (e.g. "5001") and
  // silently overwrite it with stale, broader results. fetchSeq guards
  // against that — only the response from the most recently *issued*
  // request is allowed to update state, regardless of which one resolves
  // last.
  const fetchSeq = useRef(0);
  const fetchAnimals = useCallback(() => {
    const params = [];
    if (lotFilter) params.push(`lot_id=${lotFilter}`);
    if (tagFilter) params.push(`tag=${tagFilter}`);
    const url = 'api/herd/animals' + (params.length ? '?' + params.join('&') : '');
    const seq = ++fetchSeq.current;
    Fetch(url, 'get', null,
      (d) => { if (seq === fetchSeq.current) setData(d || []); },
      () => { if (seq === fetchSeq.current) setData([]); }
    );
  }, [lotFilter, tagFilter]);

  useEffect(() => { fetchAnimals(); }, [fetchAnimals]);

  useEffect(() => {
    if (initialLotFilter != null) setLotFilter(String(initialLotFilter));
  }, [initialLotFilter]);

  useEffect(() => {
    if (initialSexFilter) setSexFilter(initialSexFilter);
  }, [initialSexFilter]);

  // Client-side filter on top of the server-fetched slice.
  const visibleData = data.filter(row => {
    if (isAliveFilter !== '' && String(row.is_alive ?? true) !== isAliveFilter) return false;
    if (pregnantFilter !== '' && String(row.is_pregnant ?? false) !== pregnantFilter) return false;
    if (breedFilter && row.breed !== breedFilter) return false;
    if (sexFilter === 'unset') { if (row.sex) return false; }
    else if (sexFilter && row.sex !== sexFilter) return false;
    if (areaFilter && row.area !== areaFilter) return false;
    if (ageFilter) {
      const range = ageCategoryToMonthRange(ageFilter);
      if (range && row.birth_date) {
        const months = ageInMonths(row.birth_date);
        if (months < range[0] || months > range[1]) return false;
      }
    }
    return true;
  });

  // breed/sex live on the animal entity; area/birth_date live on the birth
  // event — two different collections since the animals/births split.
  const onChange = (field, tag, value) => {
    const isAnimalField = field === 'breed' || field === 'sex';
    const url = isAnimalField ? `/api/animals/${tag}` : `/api/animals/${tag}/birth`;
    Fetch(url, 'PATCH', JSON.stringify({ [field]: value }), fetchAnimals, () => {});
  };

  const onDeathCauseChange = (e, tag) => {
    const death_cause = e.target.value;
    Fetch(`/api/animals/${tag}`, 'PATCH',
      JSON.stringify({ death_cause, is_alive: death_cause === '' }),
      fetchAnimals, () => {}
    );
  };

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      expanded ? next.add(row._id) : next.delete(row._id);
      return next;
    });
  };

  const toggleRowExpanded = (rowId) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  };

  const columns = [
    {
      name: t('tag'),
      selector: row => row.tag,
      sortable: true,
      cell: row => <TagCell tag={row.tag} onRenamed={fetchAnimals} />,
    },
    {
      name: t('col_lot_code'),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => formatLotCode(row.lot_id, t('herd_lot_prefix')),
    },
    {
      name: t('breed'),
      selector: row => row.breed || '',
      sortable: true,
      cell: row => (
        <BreedDropdown
          selected={row.breed}
          breeds={breeds}
          onChange={e => onChange('breed', row.tag, e.target.value)}
        />
      ),
    },
    {
      name: t('sex'),
      selector: row => row.sex || '',
      sortable: true,
      cell: row => (
        <SexDropdown
          selected={row.sex}
          onChange={e => onChange('sex', row.tag, e.target.value)}
        />
      ),
    },
    {
      name: t('area'),
      selector: row => row.area || '',
      sortable: true,
      cell: row => (
        <AreaDropdown
          selected={row.area}
          areas={areas}
          showUnknown={true}
          onChange={e => onChange('area', row.tag, e.target.value)}
        />
      ),
    },
    {
      name: t('birth_date'),
      selector: row => row.birth_date || '',
      sortable: true,
      cell: row => (
        <DateInput
          date={row.birth_date?.substring(0, 10) || ''}
          defaultToToday={false}
          required={false}
          onChange={value => onChange('birth_date', row.tag, value)}
        />
      ),
    },
    {
      name: t('age'),
      selector: row => ageInMonths(row.birth_date) ?? -1,
      sortable: true,
      cell: row => DateToAgeCategory(row.birth_date, t),
    },
    ...(isAliveFilter === '' ? [{
      name: t('dead'),
      width: '70px',
      sortable: false,
      selector: row => row.is_alive,
      cell: row => row.is_alive === false
        ? <span className="herd-status-label deceased">{t('dead')}</span>
        : null,
    }] : []),
    ...(pregnantFilter === '' ? [{
      name: t('pregnant'),
      width: '90px',
      sortable: false,
      selector: row => row.is_pregnant,
      cell: row => row.is_pregnant
        ? <span className="herd-status-label pregnant">{t('pregnant')}</span>
        : null,
    }] : []),
    ...(isAliveFilter === 'false' ? [{
      name: t('death_cause'),
      sortable: true,
      selector: row => row.death_cause || '',
      cell: row => (
        <DeathCauseDropdown
          selected={row.death_cause}
          onChange={e => onDeathCauseChange(e, row.tag)}
        />
      ),
    }] : []),
    {
      name: '',
      width: '50px',
      cell: row => {
        const hasNotes = row.notes && row.notes.length > 0;
        return (
          <span
            className={`note-icon-cell ${hasNotes ? 'has-notes' : ''}`}
            data-tooltip={t('notes')}
            onClick={() => toggleRowExpanded(row._id)}
          >
            &#128221;
          </span>
        );
      },
    },
  ];

  const activeFilterCount = [
    breedFilter, sexFilter, areaFilter, ageFilter,
    isAliveFilter !== 'true' ? isAliveFilter : undefined,
    pregnantFilter,
  ].filter(v => v !== undefined && v !== '').length;

  const resetFilters = () => {
    setBreedFilter('');
    setSexFilter('');
    setAreaFilter('');
    setAgeFilter('');
    setIsAliveFilter('true');
    setPregnantFilter('');
  };

  const LotSelect = ({ value, onChange }) => (
    <select value={value} onChange={onChange}>
      <option value="">{t('all_lots')}</option>
      {lots.map(lot => (
        <option key={lot._id} value={lot.lot_id}>
          {formatLotCode(lot.lot_id, t('herd_lot_prefix'))}
          {lot.name ? ` — ${lot.name}` : ''}
        </option>
      ))}
    </select>
  );

  const filterPanel = (
    <>
      <BreedDropdown showAll={true} breeds={breeds} selected={breedFilter} onChange={e => setBreedFilter(e.target.value)} />
      <SexDropdown showAll={true} showUnset={true} selected={sexFilter} onChange={e => setSexFilter(e.target.value)} />
      <select onChange={e => setAgeFilter(e.target.value)}>
        <option value="">{t('all_ages')}</option>
        <option value="0-12">0-12 {t('months')}</option>
        <option value="13-24">13-24 {t('months')}</option>
        <option value="25-36">25-36 {t('months')}</option>
        <option value="37+">37+ {t('months')}</option>
      </select>
      <AreaDropdown showAll={true} areas={areas} selected={areaFilter} onChange={e => setAreaFilter(e.target.value)} />
      <select value={isAliveFilter} onChange={e => setIsAliveFilter(e.target.value)}>
        <option value="">{t('all_statuses')}</option>
        <option value="true">{t('alive')}</option>
        <option value="false">{t('dead')}</option>
      </select>
      <select value={pregnantFilter} onChange={e => setPregnantFilter(e.target.value)}>
        <option value="">{t('all_statuses')}</option>
        <option value="true">{t('pregnant')}</option>
        <option value="false">{t('not_pregnant')}</option>
      </select>
    </>
  );

  return (
    <div className="ListHerd">
      {isMobile ? (
        <div className="mobile-search-filter">
          <input
            className="mobile-search-input"
            type="text"
            placeholder={t('find_tag')}
            onChange={e => setTagFilter(e.target.value)}
          />
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <input
              type="text"
              className="desktop-search-input"
              placeholder={t('find_tag')}
              onChange={e => setTagFilter(e.target.value)}
            />
            <LotSelect value={lotFilter} onChange={e => setLotFilter(e.target.value)} />
            <button
              className={`desktop-filter-toggle ${desktopFiltersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setDesktopFiltersOpen(o => !o)}
            >
              {t('filters')} {desktopFiltersOpen ? '▲' : '▼'}
              {activeFilterCount > 0 && <span className="desktop-filter-badge">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button className="desktop-filter-reset" onClick={resetFilters}>
                {t('reset_filters')}
              </button>
            )}
          </div>
          {desktopFiltersOpen && (
            <div className="desktop-filter-panel">{filterPanel}</div>
          )}
        </div>
      )}

      {isMobile ? (
        <HerdCardList
          data={visibleData.map(asBirthRow)}
          onRefresh={fetchAnimals}
        />
      ) : (
        <AppDataTable
          defaultSortFieldId={1}
          defaultSortAsc={true}
          data={visibleData}
          columns={columns}
          keyField="_id"
          expandableRows
          expandableRowsComponent={({ data: row }) => (
            <HerdExpandedComponent
              data={asBirthRow(row)}
              onNoteAdded={fetchAnimals}
              onRefresh={fetchAnimals}
            />
          )}
          expandableRowExpanded={row => expandedRowIds.has(row._id)}
          onRowExpandToggled={handleRowExpandToggled}
        />
      )}

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <LotSelect value={lotFilter} onChange={e => setLotFilter(e.target.value)} />
              {filterPanel}
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListHerdAnimals;
